import * as THREE from 'three'; // @todo optimize by just imported whats needed (see line below)
// import { MathUtils, MeshBasicMaterial, BufferGeometryUtils } from 'three';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js' // Only needed if a compressed GLB file is provided

import { GeoUtils, WGS84_ELLIPSOID, TilesRenderer } from '3d-tiles-renderer';
import { TilesFadePlugin, TileCompressionPlugin, GLTFExtensionsPlugin, CesiumIonAuthPlugin, ReorientationPlugin } from '3d-tiles-renderer/plugins';

/*function mergeTiles(tiles) {
    const geometries = [];

    tiles.group.traverse(obj => {
        if (obj.isMesh && obj.geometry){

            // Clone and apply the world matrix so the geometry is in world coords
            const g = obj.geometry.clone();
            g.applyMatrix4(obj.matrixWorld);

            geometries.push(g);
        }

    });

    if (geometries.length === 0) {
        console.log('No tiles loaded yet.');
        return;
    }

    const mergedGeometry = BufferGeometryUtils.mergeGeometries(geometries, true);
    const mergedMaterial = new THREE.MeshStandardMaterial({
		color: 0xffffff,
		flatShading: true,
		// opacity: .5,
	});

    const mergedMesh = new THREE.Mesh(mergedGeometry, mergedMaterial);
    mergedMesh.name = 'MergedTiles';
    mergedMesh.castShadow = true;
    mergedMesh.receiveShadow = true;

    return mergedMesh;
}*/

// Cube clipping
function cubeFromBoundingBox(box){
	const size = new THREE.Vector3();
	box.getSize(size);

	const mesh = new THREE.Mesh(new THREE.BoxGeometry(size.x, size.y, size.z), new THREE.MeshBasicMaterial({ 
		color: 0xff0000,
		wireframe: true 
	}));

	const center = new THREE.Vector3();
	box.getCenter(center);
	mesh.position.copy(center);

	return mesh;
}

function planeFromMesh(mesh) {
	// Make sure world matrix is up to date
	mesh.updateWorldMatrix(true, false);
	
	// Local normal of PlaneGeometry (+Z)
	const normal = new THREE.Vector3(0, 0, 1);
	
	// Transform normal to world space
	normal.applyNormalMatrix(new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld)).normalize();
  
	// World position of the plane (geometry center)
	const point = new THREE.Vector3();
	point.setFromMatrixPosition(mesh.matrixWorld);
  
	// Create the mathematical plane
	return new THREE.Plane().setFromNormalAndCoplanarPoint(normal, point);
}

function planesFromCube(cube) {
	// Ensure world matrix is up to date
	cube.updateWorldMatrix(true, false);

	const params = cube.geometry.parameters;
	const w = params.width;
	const h = params.height;
	const d = params.depth;

	const hw = w / 2;
	const hh = h / 2;
	const hd = d / 2;

	const planes = [];

	const material = new THREE.MeshBasicMaterial({
		color: 0xf0f0ff,
		side: THREE.DoubleSide,
		// wireframe: true
	});

	// Helper to create a plane
	function makePlane(geo, position, rotation) {
		const plane = new THREE.Mesh(geo, material);

		// Set local transform
		plane.position.copy(position);
		plane.rotation.copy(rotation);

		// Temporarely add as child of cube to compute transform
		cube.add(plane);

		// Wrld-space plane conversion
		// plane.updateWorldMatrix(true, false);

		// Convert to Plane
		const realPlane = planeFromMesh(plane);

		planes.push(realPlane);
		cube.remove(plane);
	}

	// +X
	makePlane(new THREE.PlaneGeometry(d, h), new THREE.Vector3(-hw, 0, 0), new THREE.Euler(0, -Math.PI / 2, 0));

	// -X
	makePlane(new THREE.PlaneGeometry(d, h), new THREE.Vector3(hw, 0, 0), new THREE.Euler(0, Math.PI / 2, 0));

	// +Z
	makePlane(new THREE.PlaneGeometry(w, h), new THREE.Vector3(0, 0, hd), new THREE.Euler(0, 0, 0));

	// -Z
	makePlane(new THREE.PlaneGeometry(w, h), new THREE.Vector3(0, 0, -hd), new THREE.Euler(0, Math.PI, 0));

	// +Y
	/*makePlane(new THREE.PlaneGeometry(w, d),new THREE.Vector3(0, hh, 0),new THREE.Euler(Math.PI / 2, 0, 0));

	// -Y
	makePlane(new THREE.PlaneGeometry(w, d),new THREE.Vector3(0, -hh, 0),new THREE.Euler(-Math.PI / 2, 0, 0));*/

	return planes;
}

// Polygon clipping
// Convert polygon points local position to world position
function getWorldPolygonVertices(mesh) {
	const pos = mesh.geometry.attributes.position;
	const vertices = [];

	for (let i = 0; i < pos.count; i++) {
		const v = new THREE.Vector3().fromBufferAttribute(pos, i);
		mesh.localToWorld(v);
		vertices.push(v);
	}

	return vertices;

	// Remove duplicates (doesnt seem to be any useful)
	/*
	const unique = [];
	const tolerance = 1e-6;

	vertices.forEach(v => {
		if (!unique.some(u => u.distanceTo(v) < tolerance)) {
			unique.push(v);
		}
	});

	return unique;
	*/
}

function planesFromPolygon(points) {
	console.log('Hi');
	const planes = [];

	for (let i = 0; i < points.length; i++) {
		const p1 = points[i];
		const p2 = points[(i + 1) % points.length];

		const edge = new THREE.Vector3().subVectors(p2, p1);

		// perpendicular in XZ plane
		const normal = new THREE.Vector3(-edge.z, 0, edge.x).normalize();

		const plane = new THREE.Plane();
		plane.setFromNormalAndCoplanarPoint(normal, p1);

		planes.push(plane);
	}

	return planes;
}

export default class Landscape{
	constructor(){
		this.terrain;
		this.buildings;
		this.location;
		this.plan;
		this.layers = [];
	}

	init (plan){
		this.plan = plan;
		this.clipPlanes = [];
		
		const settings = this.plan.settings.landscape;

		this.location = {
			// up: "+y",
			lat: settings.lat * THREE.MathUtils.DEG2RAD,
			lon: settings.lon * THREE.MathUtils.DEG2RAD,
			height: settings.height, // Higher value = model goes down
		};

		// Dispose existing intances
		this.layers.forEach((layer, index) => {
			if (layer){
				this.plan.scene.remove(this.layers[index].group);
				this.layers[index].dispose();
			}
		});

		this.layers = [];
		
		// Create bounding box
		if (settings.clip){
			switch (settings.clip?.type){
				case 'polygon':
					if (!settings.clip?.points) break;

					// Point to 2D shape
					const shape = new THREE.Shape(settings.clip.points.map(v => new THREE.Vector2(v[0], v[1])));

					// Shape to mesh
					const polygonMesh = new THREE.Mesh(new THREE.ShapeGeometry(shape), new THREE.MeshBasicMaterial({
						color: 0x00ff00,
						side: THREE.DoubleSide,
						transparent: true,
						opacity: .5,
						//   wireframe: true
					}));

					// Position it somewhere in tile space
					polygonMesh.rotation.x = Math.PI / 2;
					polygonMesh.position.set(0, 0, 0);
					polygonMesh.updateMatrixWorld(true);
					
					// Debug polygon area
					// plan.scene.add(polygonMesh);

					// Generate planes
					const polygonPoints = getWorldPolygonVertices(polygonMesh);

					// const polygonPoints = getWorldPolygonVertices(clip);
					this.clipPlanes = planesFromPolygon(polygonPoints);
				break;

				default:
					// Create cube from bbox (AABB > OOBB)
					const cube = cubeFromBoundingBox(this.plan.bbox);
					
					// Replicate model trasform to cube
					cube.applyMatrix4(this.plan.model.matrixWorld);

					// Create planes from cube
					this.clipPlanes = planesFromCube(cube);
					
					// Debug

					// Bounding box (AABB)
					// this.plan.scene.add(new THREE.Box3Helper(this.plan.bbox, 0x00ff00));
					
					// Cube (OOBB)
					// this.plan.scene.add(cube);
					
					// Planes
					// this.clipPlanes.forEach(plane => {
						// 	plan.scene.add(new THREE.PlaneHelper(plane, 100, 0xff0000));
					// });
					

					/* Previous system with manual positioning cube, (position, rotation and scale stored in clip settings)
					// Create cube from bounding box
					const cube = new THREE.Mesh(new THREE.BoxGeometry(settings.clip.dimension.x, settings.clip.dimension.y, settings.clip.dimension.z), new THREE.MeshBasicMaterial({
						color: 0xf0ff0f,
						transparent: true,
						opacity: .5,
					}));

					cube.position.set(settings.clip.position.x, settings.clip.position.y, settings.clip.position.z);
					cube.rotation.y = THREE.MathUtils.degToRad(settings.clip.rotation);
					*/
				break;
			}
		}
		
		// Materials
		const materialBuildings = new THREE.MeshStandardMaterial({
			color: 0xcccccc,
			metalness: 0,
			roughness: 1
		});


		const materialTerrain = new THREE.MeshStandardMaterial({
			color: 0xffffff,
			metalness: 0,
			roughness: 1,
		});

				
		// Clip
		if (this.clipPlanes.length){
			// Buliding
			materialBuildings.clippingPlanes = this.clipPlanes;
			materialBuildings.clipIntersection = true;
			materialBuildings.clipShadows = true;
			
			// Terrain
			materialTerrain.clippingPlanes = this.clipPlanes;
			materialTerrain.clipIntersection = true;
			materialTerrain.clipShadows = true;
			
			// if (DEV){
				// this.clipPlanes.forEach(plane => {
				// 	plan.scene.add(new THREE.PlaneHelper(plane, 100, 0xff0000));
				// });
			// }
		}

		// 2275207 - 3D Tiles - Google Maps
		// 96188 - 3D Tiles - Ceisum buildings
		// 2521176 - 3D Tiles - Cesium buildings CWB
		// 1 - Terrain - Cesium
		// 2426648 Terrain - Cesium Bathymetry
		
		switch (settings.type){
			case 'real':				
				materialTerrain.flatShading = true;
				
				if (settings.emissiveIntensity){
					materialTerrain.emissive.setHex(0xffffff);
					materialTerrain.emissiveIntensity = settings.emissiveIntensity;
				}

				if (settings.opacity < 1){
					materialTerrain.transparent = true;
					materialTerrain.opacity = settings.opacity;
				}

				this.layers.push(this.create('2275207', materialTerrain));
			break;
			
			case 'clay':
				materialTerrain.flatShading = true;
				this.layers.push(this.create('2275207', materialTerrain));
			break;
			
			case 'lowpoly':
				this.layers.push(this.create('1', materialTerrain));
				this.layers.push(this.create('96188', materialBuildings));
			break;
		}

		plan.update.landscape = true;
	}

	create (assetId, material){
		const ion = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiIxNDE0ODdhYy0zMGZlLTRmMmItYTRhYi03NGU1ZWQyY2E5MTMiLCJpZCI6MzYzNjk1LCJpYXQiOjE3NjQwNjU5MDB9.lFolRtVt4RN_JFgLtPn55mCy6SG_9h3x2k3uJj3sZRQ';
		const tiles = new TilesRenderer();
		
		// tiles.maxDepth = 25;

		tiles.registerPlugin(new CesiumIonAuthPlugin({apiToken: ion, assetId: assetId, autoRefreshToken: true}));
		tiles.registerPlugin(new TileCompressionPlugin());
		tiles.registerPlugin(new TilesFadePlugin());
		tiles.registerPlugin(new GLTFExtensionsPlugin({dracoLoader: new DRACOLoader().setDecoderPath('https://www.gstatic.com/draco/v1/decoders/')}));
		tiles.registerPlugin(new ReorientationPlugin(this.location));

		tiles.localClippingEnabled = true;
		// tiles.group.rotation.y = Math.PI;
		tiles.group.rotateY(Math.PI);
		tiles.group.updateMatrixWorld(true);

		tiles.addEventListener('load-model', (event) => {
			tiles.group.traverse(object => {
				if (object.isMesh){
					object.castShadow = true;
					object.receiveShadow = true;
				}
				
				if (material && object.material) {
					const mat = material.clone();
					mat.map = object.material.map;
					object.material = mat;
					
					// Regular clay
					// object.material = material;
				}

				if (!material && object.material){ // clay under
					// object.material.transparent = true;
					// object.material.opacity = .2;
				}
			});
		});

		tiles.addEventListener('needs-update', () => {
			// console.log( 'needs-update' );
			this.plan.update.landscape = true;
			this.plan.render();
		});

		tiles.addEventListener( 'dispose-model', ({scene}) => {
			console.log( 'dispose-model' );

			// dispose of any manually created materials
			scene.traverse( c => {
				if ( c.material ) {
					c.material.dispose();
				}
			});
		});

		tiles.setResolutionFromRenderer(this.plan.camera, this.plan.renderer);
		tiles.setCamera(this.plan.camera);

		return tiles;
	}

	update(camera, renderer){
		this.layers.forEach(layer => {
			layer.setResolutionFromRenderer(camera, renderer);
			layer.setCamera(camera);
			layer.update();
		});
/*
		if (this.terrain){
			this.terrain.setResolutionFromRenderer(camera, renderer);
			this.terrain.setCamera(camera);
			this.terrain.update();
		}
		
		if (this.buildings){
			this.buildings.setResolutionFromRenderer(camera, renderer);
			this.buildings.setCamera(camera);
			this.buildings.update();
		}*/
	}
}